<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {codePreview, formatDate, showSuccessMessage, validatePhoneNumber} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Textarea from '@/components/forms/Textarea.vue';
import Select from '@/components/forms/Select.vue';
import {authData} from '@/services/AuthService';
import {bondsEnabled} from '@/utils/features';
import Input from "@/components/forms/Input.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import router from "@/router";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import StrategyService from "@/services/StrategyService";
import ContractTypeService from "@/services/ContractTypeService";
import {useI18n} from 'vue-i18n';
import IntermediaryService from "@/services/IntermediaryService";

const {t, te} = useI18n();

const props = defineProps({
  strategy: {type: Object, required: false},
});

const strategy = ref({strategy_name: '', name: '', code: '', product_type: 'STRATEGY', is_active: false, is_active_all_contract_types: false, contract_types: [], contract_types_data: [], performance: 0, description: ''});
const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);

const contractTypes = ref([]);
const intermediaries = ref([]);

const clientTypes = ref(['RETAIL', 'PROFESSIONAL', 'AUTHORIZED_OPPOSITE_SIDE']);
const adequacyTestItems = ref(['REFUSED_ANSWER', 'BASIC_INVESTOR', 'INFORMED_INVESTOR', 'ADVANCED_INVESTOR']);
const abilityToLossOptions = ref([/*'NOT_ABLE_TO_LOSE', */'CAN_LOSE']);
const clientRisk = ref([
  {key: 1, value: '1_CONSERVATIVE'},
  {key: 2, value: '2_CONSERVATIVE'},
  {key: 3, value: '3_BALANCED'},
  {key: 4, value: '4_BALANCED'},
  {key: 5, value: '5_BALANCED'},
  {key: 6, value: '6_DYNAMIC'},
  {key: 7, value: '7_DYNAMIC'}
]);
const clientInvestmentGoals = ref(['PROTECTION_OF_FINANCE', 'FINANCIAL_RESERVE', 'VALUATION']);
const clientEsg = ref(['WITHOUT_ESG', 'WITH_ESG']);
const investmentHorizonOptions = ref(['NOT_SPECIFIED', 1, 3, 5, 8, 10]);
const investmentPreferences = ref(['NO_LOSS_GUARANTEE', 'PROTECTION', 'GROWTH']);

const isActiveAllContractTypesCustomDate = ref(false);

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', strategy.value);
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

if (props.strategy) {
  strategy.value = {...props.strategy};
  strategy.value.strategy_name = strategy.value.name;
  if (props.strategy.is_active_all_contract_types) {
    isActiveAllContractTypesCustomDate.value = !!(props.strategy.is_active_all_contract_types_from || props.strategy.is_active_all_contract_types_to);
    if (props.strategy.is_active_all_contract_types_from) {
      strategy.value.is_active_all_contract_types_from = props.strategy.is_active_all_contract_types_from.slice(0, 16);
    }
    if (props.strategy.is_active_all_contract_types_to) {
      strategy.value.is_active_all_contract_types_to = props.strategy.is_active_all_contract_types_to.slice(0, 16);
    }
  }
}

onMounted(() => {
  fetchContractTypes();
  fetchIntermediaries();
  if (bondsAvailable) {
    fetchBondStrategy();
  }
});

// IMA-517: typ produktu – strategie typu Dluhopisy smí existovat jen jedna (BE StrategySaveRequest)
// typ uložený při otevření formuláře – podle něj upozorníme na změnu typu u existující strategie
const originalProductType = props.strategy?.product_type ?? 'STRATEGY';
// jiná existující strategie typu Dluhopisy (aktivní i neaktivní), nebo null
const otherBondStrategy = ref(null);
// dluhopisy nejsou v B.I.G. Invest SK/CZ – typ strategie Dluhopisy se tam nenabízí (BE ho odmítne)
const bondsAvailable = bondsEnabled(authData.loggedUser);

function fetchBondStrategy() {
  StrategyService.getBondStrategy().then(bondStrategy => {
    // ukládaná strategie se nepočítá – u ní volba Dluhopisy zůstává
    otherBondStrategy.value = bondStrategy && bondStrategy.id !== props.strategy?.id ? bondStrategy : null;
  }).catch(() => {
    otherBondStrategy.value = null;
  });
}

/** Volbu Dluhopisy nelze vybrat, když už jiná strategie typu Dluhopisy existuje. */
const bondTypeDisabled = computed(() => otherBondStrategy.value !== null && strategy.value.product_type !== 'BOND');
/** Změna typu u uložené strategie mění zařazení jejích smluvních strategií (výkonnost, přehledy). */
const productTypeChanged = computed(() => !!props.strategy?.id && (strategy.value.product_type ?? 'STRATEGY') !== originalProductType);

/*function deleteStrategy() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: 'Smazání strategie',
    message: 'Opravdu chcete smazat tento záznam? Tato akce je nevratná.',
  });
  onConfirm(() => {
    StrategyService.deleteStrategy(strategy.value.id).then(() => {
      showSuccessMessage('Strategie byla smazána.');
      router.push({name: 'Strategies'});
    });
  });
  reveal();
}*/

function deactivateStrategy() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: 'Deaktivace strategie',
    message: 'Opravdu chcete deaktivovat tuto strategii? Tato akce je nevratná.',
  });
  onConfirm(() => {
    StrategyService.deactivateStrategy(strategy.value.id).then(() => {
      showSuccessMessage('Strategie byla deaktivována.');
      router.push({name: 'Strategies'});
    });
  });
  reveal();
}

function fetchContractTypes() {
  ContractTypeService.getAll().then(response => {
    contractTypes.value = response;
  });
}

function fetchIntermediaries() {
  IntermediaryService.getAll().then(response => {
    intermediaries.value = response;
  });
}

function openModal() {
  isOpen.value = true
}

const getContractTypeName = (id) => {
  const found = contractTypes.value.find((t) => t.id === id);
  return found ? found.name : '';
};

watch(
    () => strategy.value.contract_types,
    (newIds) => {
      const updated = newIds.map((id) => {
        const existing = strategy.value.contract_types_data?.find((item) => item.id === id);
        return existing || {id, from: null, to: null, use_custom_date: false};
      });
      strategy.value.contract_types_data = updated;
    },
    {immediate: true}
);
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModalWith('cancel')" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="strategy.id" as="h3" class="modal-title">
                {{ t('createUpdateStrategyModal.strategy_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateStrategyModal.strategy_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="strategyNameInput" class="form-label">{{ t('createUpdateStrategyModal.strategy_name') }}</label>
                    <input v-model="strategy.strategy_name" id="strategyNameInput" class="form-control" required/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="codeInput" class="form-label">{{ t('createUpdateStrategyModal.code') }}</label>
                    <input v-model="strategy.code" id="codeInput" class="form-control" maxlength="3"/>
                  </div>
                </div>
                <!-- typ produktu (Strategie / Dluhopisy) jen v instanci s dluhopisy; existující strategie Dluhopisy ho ukáže vždy -->
                <div v-if="bondsAvailable || strategy.product_type === 'BOND'" class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="productTypeSelect" class="form-label">{{ t('createUpdateStrategyModal.product_type') }}</label>
                    <select v-model="strategy.product_type" id="productTypeSelect" class="form-select">
                      <option value="STRATEGY">{{ t('createUpdateStrategyModal.product_type_strategy') }}</option>
                      <option value="BOND" :disabled="bondTypeDisabled">{{ t('createUpdateStrategyModal.product_type_bond') }}</option>
                    </select>
                    <small class="text-muted d-block mt-1">{{ t('createUpdateStrategyModal.product_type_bond_hint') }}</small>
                    <small v-if="bondTypeDisabled" class="text-muted d-block">{{ t('createUpdateStrategyModal.product_type_bond_exists', {name: otherBondStrategy.name}) }}</small>
                    <div v-if="productTypeChanged" class="alert alert-warning py-2 small mt-2 mb-0">{{ t('createUpdateStrategyModal.product_type_changed_warning') }}</div>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="externalIdInput" class="form-label">{{ t('createUpdateStrategyModal.external_id') }}</label>
                    <input v-model="strategy.external_id" id="externalIdInput" class="form-control" type="number"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12">
                    <div class="form-check">
                      <label for="isActiveCheckbox" class="form-label">{{ t('createUpdateStrategyModal.active') }}</label>
                      <input type="checkbox" v-model="strategy.is_active" id="isActiveCheckbox" class="form-check-input"/></div>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="performanceInput" class="form-label">{{ t('createUpdateStrategyModal.performance') }}</label>
                    <input v-model="strategy.performance" id="performanceInput" class="form-control" type="number" step="0.001" min="0" max="100"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="riskProfileInput" class="form-label">{{ t('createUpdateStrategyModal.risk_profile') }}</label>
                    <input v-model="strategy.risk_profile" id="riskProfileInput" class="form-control" type="number" step="1" min="1" max="50"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="descriptionInput" class="form-label">{{ t('createUpdateStrategyModal.description') }}</label>
                    <input v-model="strategy.description" id="descriptionInput" class="form-control"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="contractTypesSelect" class="form-label">{{ t('createUpdateStrategyModal.intermediaries_can_sell') }}</label>
                    <select v-model="strategy.available_for_intermediaries" id="contractTypesSelect" class="form-select" multiple>
                      <option v-for="intermediary in intermediaries" :key="intermediary.id" :value="intermediary.id">{{ intermediary.intermediary_name }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12">
                    <div class="form-check">
                      <label for="isActiveAllContractTypesCheckbox" class="form-label">{{ t('createUpdateStrategyModal.active_for_all_contract_types') }}</label>
                      <input type="checkbox" v-model="strategy.is_active_all_contract_types" id="isActiveAllContractTypesCheckbox" class="form-check-input"/></div>
                  </div>
                  <div v-if="strategy.is_active_all_contract_types" class="col-12 form-group">
                    <fieldset class="pb-2">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" id="switchIsActiveAllContractTypes" v-model="isActiveAllContractTypesCustomDate"/>
                        <label class="form-check-label" for="switchIsActiveAllContractTypes">Nastavit období</label>
                      </div>
                      <div v-if="isActiveAllContractTypesCustomDate" class="row form-group">
                        <div class="form-group col-6">
                          <label for="isActiveAllContractTypesFromDateInput" class="form-label">Od:</label>
                          <input v-model="strategy.is_active_all_contract_types_from" id="isActiveAllContractTypesFromDateInput" class="form-control" type="datetime-local"/>
                        </div>
                        <div class="form-group col-6">
                          <label for="isActiveAllContractTypesToDateInput" class="form-label">Do:</label>
                          <input v-model="strategy.is_active_all_contract_types_to" id="isActiveAllContractTypesToDateInput" class="form-control" type="datetime-local"/>
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
                <div v-if="!strategy.is_active_all_contract_types" class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="contractTypesSelect" class="form-label">{{ t('createUpdateStrategyModal.type_of_contracts') }}</label>
                    <select v-model="strategy.contract_types" id="contractTypesSelect" class="form-select" multiple>
                      <option v-for="type in contractTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                    </select>
                  </div>
                  <div v-for="type in strategy.contract_types_data" :key="type.id" class="col-12 col-md-6">
                    <div class="mb-4 border p-3 rounded bg-gray-100">
                      <h5 class="mb-2">{{ getContractTypeName(type.id) }}</h5>

                      <div class="form-check form-switch mb-2">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'switch_' + type.id"
                            v-model="type.use_custom_date"
                        />
                        <label class="form-check-label" :for="'switch_' + type.id">{{ t('createUpdateStrategyModal.set_period') }}</label>
                      </div>

                      <div v-if="type.use_custom_date">
                        <div class="mb-2">
                          <label class="form-label">Od:</label>
                          <input v-model="type.from" type="datetime-local" class="form-control"/>
                        </div>
                        <div>
                          <label class="form-label">Do:</label>
                          <input v-model="type.to" type="datetime-local" class="form-control"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="clientTypesSelect" class="form-label">{{ t('createUpdateStrategyModal.client_type') }}</label>
                    <select v-model="strategy.client_type" id="clientTypesSelect" class="form-select" multiple>
                      <option v-for="type in clientTypes" :key="type" :value="type">{{ te('createUpdateStrategyModal.' + type) ? t('createUpdateStrategyModal.' + type) : type }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="adequacyTestSelect" class="form-label">{{ t('createUpdateStrategyModal.adequacy_test') }}</label>
                    <select v-model="strategy.adequacy_test" id="adequacyTestSelect" class="form-select" multiple>
                      <option v-for="item in adequacyTestItems" :key="item" :value="item">{{ te('createUpdateStrategyModal.' + item) ? t('createUpdateStrategyModal.' + item) : item }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="clientAbilityToLossSelect" class="form-label">{{ t('createUpdateStrategyModal.ability_to_bear_loss') }}</label>
                    <select v-model="strategy.ability_to_bear_loss" id="clientAbilityToLossSelect" class="form-select" multiple>
                      <option v-for="item in abilityToLossOptions" :key="item" :value="item">{{ te('createUpdateStrategyModal.' + item) ? t('createUpdateStrategyModal.' + item) : item }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="clientRiskSelect" class="form-label">{{ t('createUpdateStrategyModal.risk_tolerance_correction') }}</label>
                    <select v-model="strategy.risk_tolerance_correction" id="clientRiskSelect" class="form-select" multiple>
                      <option v-for="item in clientRisk" :key="item.key" :value="item.key">{{ te('createUpdateStrategyModal.' + item.value) ? t('createUpdateStrategyModal.' + item.value) : item.value }}</option>
                    </select>
                  </div>
                </div>
                <!--                <div class="row mb-3">
                                  <div class="col-12 form-group">
                                    <label for="riskToleranceCorrectionInput" class="form-label">{{ t('createUpdateStrategyModal.risk_tolerance_correction') }}</label>
                                    <input v-model="strategy.risk_tolerance_correction" id="riskToleranceCorrectionInput" class="form-control" type="number" step="1"/>
                                  </div>
                                </div>-->
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="investmentHorizonSelect" class="form-label">{{ t('createUpdateStrategyModal.investment_horizon') }}</label>
                    <select v-model="strategy.investment_horizon" id="investmentHorizonSelect" class="form-select" multiple>
                      <option v-for="item in investmentHorizonOptions" :key="item" :value="item">{{ typeof item === 'number' ? item : t('createUpdateStrategyModal.' + item) }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="clientInvestmentGoalsSelect" class="form-label">{{ t('createUpdateStrategyModal.client_investment_goals') }}</label>
                    <select v-model="strategy.client_investment_goals" id="clientInvestmentGoalsSelect" class="form-select" multiple>
                      <option v-for="item in clientInvestmentGoals" :key="item" :value="item">{{ te('createUpdateStrategyModal.' + item) ? t('createUpdateStrategyModal.' + item) : item }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="clientInvestmentPreferencesSelect" class="form-label">{{ t('createUpdateStrategyModal.investment_preferences') }}</label>
                    <select v-model="strategy.investment_preferences" id="clientInvestmentPreferencesSelect" class="form-select" multiple>
                      <option v-for="item in investmentPreferences" :key="item" :value="item">{{ te('createUpdateStrategyModal.' + item) ? t('createUpdateStrategyModal.' + item) : item }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="clientESGSelect" class="form-label">{{ t('createUpdateStrategyModal.client_esg') }}</label>
                    <select v-model="strategy.client_esg" id="clientESGSelect" class="form-select" multiple>
                      <option v-for="item in clientEsg" :key="item" :value="item">{{ te('createUpdateStrategyModal.' + item) ? t('createUpdateStrategyModal.' + item) : item }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateStrategyModal.cancel') }}</button>
                <button v-if="strategy.id" class="btn btn-danger btn-md" @click.prevent="deactivateStrategy">{{ t('createUpdateStrategyModal.deactivate') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateStrategyModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

